/**
 * Lot Size Selector Web Component
 * Handles color selection, product lookup by color/size/lot, and add to cart functionality
 */

if (!customElements.get('lot-size-selector')) {
    class LotSizeSelector extends HTMLElement {
        constructor() {
            super();
            this.currentColor = '';
            this.selectedSize = null;
            this.selectedProduct = null;
            this.sizeProductMap = new Map();
            this.productCache = new Map();
        }

        connectedCallback() {
            this.currentColor = this.dataset.currentColor || '';
            this.baseHandle = this.dataset.baseHandle || 'classico';
            this.maxLots = parseInt(this.dataset.maxLots) || 4;

            this.bindElements();
            this.bindEvents();
            this.restoreSelectedSize();
            this.initializeSizes();
        }

        bindElements() {
            this.colorButtons = this.querySelectorAll('[ref="colorButtons[]"]');
            this.sizeButtons = this.querySelectorAll('[ref="sizeButtons[]"]');
            this.lotInfo = this.querySelector('[ref="lotInfo"]');
            this.lotBadge = this.querySelector('[ref="lotBadge"]');
            this.lotBadgeText = this.querySelector('[ref="lotBadgeText"]');
            this.lotTooltip = this.querySelector('[ref="lotTooltip"]');
            this.lotTooltipText = this.querySelector('[ref="lotTooltipText"]');
            this.addToCartBtn = this.querySelector('[ref="addToCartBtn"]');
            this.addToCartLoading = this.querySelector('[ref="addToCartLoading"]');
            this.variantIdInput = this.querySelector('[ref="variantIdInput"]');
            this.selectedSizeInput = this.querySelector('[ref="selectedSizeInput"]');
            this.dispatchNotice = this.querySelector('[ref="dispatchNotice"]');
            this.dispatchDateText = this.querySelector('[ref="dispatchDateText"]');

            // Parse dispatch dates from metafield
            this.dispatchDates = {};
            try {
                const dispatchDatesAttr = this.dataset.dispatchDates;

                if (dispatchDatesAttr && dispatchDatesAttr !== '{}') {
                    // Decode HTML entities and parse JSON
                    const decoded = dispatchDatesAttr
                        .replace(/&quot;/g, '"')
                        .replace(/&#34;/g, '"')
                        .replace(/&amp;/g, '&')
                        .replace(/&#39;/g, "'")
                        .replace(/&lt;/g, '<')
                        .replace(/&gt;/g, '>');

                    this.dispatchDates = JSON.parse(decoded);
                }
            } catch (e) {
                console.warn('[LOT-SELECTOR] Failed to parse dispatch dates:', e);
            }
        }

        bindEvents() {
            this.colorButtons.forEach((btn) => {
                btn.addEventListener('click', () => this.handleColorClick(btn));
            });

            this.sizeButtons.forEach((btn) => {
                btn.addEventListener('click', () => this.handleSizeClick(btn));
            });

            if (this.addToCartBtn) {
                this.addToCartBtn.addEventListener('click', () => this.handleAddToCart());
            }
        }

        restoreSelectedSize() {
            const urlParams = new URLSearchParams(window.location.search);
            const savedSize = urlParams.get('size') || sessionStorage.getItem('lot-selector-size');

            if (savedSize && this.selectedSizeInput) {
                this.selectedSizeInput.value = savedSize;
            }
        }

        async initializeSizes() {
            if (!this.currentColor) {
                console.warn('[LOT-SELECTOR] No color detected');
                this.sizeButtons.forEach(btn => {
                    btn.disabled = true;
                    btn.dataset.unavailable = 'true';
                });
                return;
            }

            const promises = Array.from(this.sizeButtons).map((btn) =>
                this.checkSizeAvailability(btn)
            );

            await Promise.all(promises);

            // Auto-select saved size or first available
            const savedSize = this.selectedSizeInput?.value;
            let autoSelectBtn = null;

            if (savedSize) {
                autoSelectBtn = Array.from(this.sizeButtons).find(
                    btn => btn.dataset.size === savedSize && btn.dataset.unavailable !== 'true'
                );
            }

            if (!autoSelectBtn) {
                autoSelectBtn = Array.from(this.sizeButtons).find(
                    btn => btn.dataset.unavailable !== 'true'
                );
            }

            if (autoSelectBtn) {
                this.handleSizeClick(autoSelectBtn);
            }
        }

        async checkSizeAvailability(btn) {
            const sizeHandle = btn.dataset.sizeHandle;

            for (let lot = 1; lot <= this.maxLots; lot++) {
                const handle = `${this.baseHandle}-${this.currentColor}-${sizeHandle}-lt${lot}`;
                const product = await this.fetchProduct(handle);

                if (product && product.available) {
                    this.sizeProductMap.set(sizeHandle, { lot, product });
                    btn.disabled = false;
                    btn.dataset.unavailable = 'false';
                    return;
                }
            }

            // No lot found with stock
            btn.disabled = true;
            btn.dataset.unavailable = 'true';
        }

        async fetchProduct(handle) {
            if (this.productCache.has(handle)) {
                return this.productCache.get(handle);
            }

            try {
                // FIXED: Use .js endpoint instead of .json (Shopify Ajax API)
                const url = `${window.Shopify?.routes?.root || '/'}products/${handle}.js`;
                const response = await fetch(url);

                if (!response.ok) {
                    this.productCache.set(handle, null);
                    return null;
                }

                const product = await response.json();

                // Get first variant availability
                const firstVariant = product.variants?.[0];
                const available = product.available || firstVariant?.available || false;

                const result = {
                    id: product.id,
                    title: product.title,
                    handle: product.handle,
                    available,
                    variantId: firstVariant?.id,
                    price: firstVariant?.price || product.price,
                    compareAtPrice: firstVariant?.compare_at_price
                };

                this.productCache.set(handle, result);
                return result;
            } catch (e) {
                this.productCache.set(handle, null);
                return null;
            }
        }

        handleColorClick(btn) {
            const newColor = btn.dataset.color;

            if (newColor === this.currentColor) return;

            if (this.selectedSize) {
                sessionStorage.setItem('lot-selector-size', this.selectedSize);
            }

            const colorHandle = btn.dataset.colorHandle;
            const sizeParam = this.selectedSize ? `?size=${encodeURIComponent(this.selectedSize)}` : '';
            window.location.href = `/products/${colorHandle}${sizeParam}`;
        }

        handleSizeClick(btn) {
            if (btn.disabled || btn.dataset.unavailable === 'true') return;

            this.sizeButtons.forEach(b => b.dataset.selected = 'false');
            btn.dataset.selected = 'true';

            const sizeHandle = btn.dataset.sizeHandle;
            const size = btn.dataset.size;
            const data = this.sizeProductMap.get(sizeHandle);

            if (data) {
                this.selectedSize = size;
                this.selectedProduct = data.product;

                if (this.selectedSizeInput) {
                    this.selectedSizeInput.value = size;
                }

                if (this.variantIdInput) {
                    this.variantIdInput.value = data.product.variantId;
                }

                if (this.addToCartBtn) {
                    this.addToCartBtn.disabled = false;
                }

                if (this.lotInfo) {
                    this.lotInfo.hidden = false;
                }
                if (this.lotBadge) {
                    this.lotBadge.dataset.lot = data.lot;
                }
                if (this.lotBadgeText) {
                    this.lotBadgeText.textContent = `LOTE ${data.lot}`;
                }

                // Update tooltip with dispatch date
                const lotKey = `lt${data.lot}`;
                const dispatchDate = this.dispatchDates[lotKey] || null;

                if (this.lotTooltipText) {
                    if (dispatchDate) {
                        this.lotTooltipText.textContent = `Previsão de despacho: ${dispatchDate}`;
                    } else {
                        this.lotTooltipText.textContent = `Previsão de despacho: A definir`;
                    }
                }

                // Update dispatch notice below add to cart button
                if (this.dispatchNotice) {
                    if (dispatchDate) {
                        this.dispatchNotice.hidden = false;
                        if (this.dispatchDateText) {
                            this.dispatchDateText.textContent = dispatchDate;
                        }
                    } else {
                        this.dispatchNotice.hidden = true;
                    }
                }

                sessionStorage.setItem('lot-selector-size', size);
            }
        }

        async handleAddToCart() {
            if (!this.selectedProduct || !this.selectedProduct.variantId) return;

            // Disable button and show loading
            if (this.addToCartBtn) {
                this.addToCartBtn.disabled = true;
                this.addToCartBtn.dataset.adding = 'true';
            }
            if (this.addToCartLoading) {
                this.addToCartLoading.hidden = false;
            }

            // Get cart items section IDs for section rendering
            const cartItemsComponents = document.querySelectorAll('cart-items-component');
            let sectionIds = [];
            cartItemsComponents.forEach((item) => {
                if (item.dataset.sectionId) {
                    sectionIds.push(item.dataset.sectionId);
                }
            });

            try {
                const formData = new FormData();
                formData.append('id', this.selectedProduct.variantId);
                formData.append('quantity', '1');
                if (sectionIds.length > 0) {
                    formData.append('sections', sectionIds.join(','));
                }

                const response = await fetch(window.Theme?.routes?.cart_add_url || '/cart/add.js', {
                    method: 'POST',
                    body: formData,
                    headers: {
                        'Accept': 'application/json'
                    }
                });

                const result = await response.json();

                if (result.status) {
                    // Error response
                    console.error('Add to cart error:', result.message);
                    throw new Error(result.message || 'Failed to add to cart');
                }

                // Success - dispatch cart:update event to trigger drawer and animations
                const cartUpdateEvent = new CustomEvent('cart:update', {
                    bubbles: true,
                    detail: {
                        resource: result,
                        sourceId: this.id,
                        data: {
                            source: 'lot-size-selector',
                            itemCount: 1,
                            productId: this.selectedProduct.id,
                            sections: result.sections
                        }
                    }
                });
                document.dispatchEvent(cartUpdateEvent);

                // Show added state
                if (this.addToCartBtn) {
                    this.addToCartBtn.dataset.added = 'true';
                    setTimeout(() => {
                        if (this.addToCartBtn) {
                            this.addToCartBtn.removeAttribute('data-added');
                        }
                    }, 1500);
                }

            } catch (e) {
                console.error('Add to cart failed:', e);
                // Show error message
                if (this.addToCartBtn) {
                    const originalText = this.addToCartBtn.querySelector('.lot-size-selector__add-btn-text');
                    if (originalText) {
                        const original = originalText.textContent;
                        originalText.textContent = 'Erro - Tente novamente';
                        setTimeout(() => {
                            originalText.textContent = original;
                        }, 2000);
                    }
                }
            } finally {
                if (this.addToCartBtn) {
                    this.addToCartBtn.disabled = false;
                    this.addToCartBtn.removeAttribute('data-adding');
                }
                if (this.addToCartLoading) {
                    this.addToCartLoading.hidden = true;
                }
            }
        }
    }

    customElements.define('lot-size-selector', LotSizeSelector);
}
